import React from 'react';
import { Button } from '@vfs-digital-channels/ns-react-components';
import { useIsMobile } from '../../../common/hooks/use-is-mobile';

type Props = {
  visible: boolean;
  heading: string;
  showCloseButton?: boolean;
  subtext?: string;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  automationIdPrefix?: string;
  children?: React.ReactNode;
};

export function SummaryConfirmModal(props: Props) {
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const isMobile = useIsMobile();

  const handleClose = () => {
    if (props.onCancel) {
      props.onCancel();
    }
    if (dialogRef && dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const handleConfirm = () => {
    if (props.onConfirm) {
      props.onConfirm();
    }
    if (dialogRef && dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();
    if (event.key == 'Escape') {
      props.onCancel && props.onCancel();
      dialogRef.current?.close();
    }
    return;
  };

  React.useEffect(() => {
    if (props.visible && dialogRef) {
      dialogRef.current?.showModal();
    }
  }, [props.visible]);

  React.useEffect(() => {
    dialogRef.current?.addEventListener('keydown', handleKeyDown);

    return () => {
      dialogRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className={`${
        props.visible ? 'flex flex-col' : 'hidden'
      } items-center justify-center sm:w-full w-[90%] p-4 sm:p-20 bg-white gap-y-4 max-w-160 min-w-84 rounded-20 backdrop:opacity-80 backdrop:bg-black-pure min-h-[23rem]`}
    >
      {props.heading && (
        <h1 className="text-3xl font-medium text-center text-black sm:text-4xl">{props.heading}</h1>
      )}
      {props.subtext && (
        <p className="text-base font-light text-center sm:text-2xl text-grey-500">
          {props.subtext}
        </p>
      )}

      <div className="flex flex-col items-center justify-center w-full sm:flex-row sm:justify-between gap-y-4">
        {props.onCancel && (
          <Button variant="outline" size={isMobile ? 'sm' : 'lg'} onClick={handleClose}>
            {props.cancelButtonLabel ?? 'Cancel'}
          </Button>
        )}
        {props.onConfirm && (
          <Button variant="primary" size={isMobile ? 'sm' : 'lg'} onClick={handleConfirm}>
            {props.confirmButtonLabel ?? 'Confirm'}
          </Button>
        )}
      </div>
    </dialog>
  );
}
